import Carousel, { useCarousel } from '@orn/shared-web/components/Carousel';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { ChevronLeftIcon, ChevronRightIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { calloutToAlertRow } from '../../utils/alertRow';
import { ALERT_SECTION_LABELS } from '../../utils/alerts';
import AlertListItem from '../AlertListItem';
import AlertTypeIcon from '../AlertTypeIcon';

import type { ListedAlert } from '../../utils/alertRow';

/** What the strip renders. Shared with the Alerts Center so the two rows cannot drift. */
export type AssistantAlert = ListedAlert;

/** Reserves 44px for the row gap plus the next row's peek — see this folder's README § Layout. */
const PEEK_CLASS = 'basis-[calc(100%-2.75rem)]';

const CHEVRON_CLASS = 'text-fg-quaternary cursor-pointer disabled:cursor-default disabled:opacity-30';

/**
 * Fades the peeking row out against the ground behind the strip — the app
 * ground in the `xl+` side panel, the pane card below `xl` where the assistant
 * is the full-page route. Only while there is somewhere to scroll — on the
 * last row the fade would just dim its trailing edge.
 */
const TrackFade = () => {
  const { canScrollNext } = useCarousel();

  if (!canScrollNext) {
    return null;
  }

  return (
    <div className="from-bg-app_light xl:from-bg-app pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-l to-transparent" />
  );
};

interface ICarouselPagerProps {
  total: number;
  /** Chevrons are a pointer affordance; touch widths swipe the track instead. */
  withChevrons: boolean;
}

/**
 * `n of total`, flanked by chevrons where there is room for them. Lives inside
 * `Carousel.Root` because it reads the live snap index off the carousel context.
 */
const CarouselPager = ({ total, withChevrons }: ICarouselPagerProps) => {
  const { selectedIndex } = useCarousel();

  return (
    <div className="flex shrink-0 items-center gap-2">
      {withChevrons && (
        <Carousel.PrevTrigger className={CHEVRON_CLASS}>
          <ChevronLeftIcon className="size-5 shrink-0" />
        </Carousel.PrevTrigger>
      )}
      <span className="text-tertiary whitespace-nowrap text-sm">
        {selectedIndex + 1} of {total}
      </span>
      {withChevrons && (
        <Carousel.NextTrigger className={CHEVRON_CLASS}>
          <ChevronRightIcon className="size-5 shrink-0" />
        </Carousel.NextTrigger>
      )}
    </div>
  );
};

interface IAssistantAlertCarouselBaseProps {
  alerts: AssistantAlert[];
  /** Id of the row drawn with the brand border. */
  selectedId?: string | null;
  /** Id of the row showing the mid-turn refusal pill, or none — see `AlertListItem`. */
  blockedNoticeAlertId?: string | null;
  onSelect?: (alert: AssistantAlert) => void;
  onDismiss?: (alertId: string) => void;
  /** Opens the Alerts Center from the header's "View all" link. */
  onViewAll: () => void;
  className?: string;
}

/**
 * Horizontal strip of active alerts that sits above the assistant composer:
 * a labeled header with a pager and a "View all" link, over one
 * {@link AlertListItem} per alert with the next one peeking under a right-edge
 * fade. Purely presentational — {@link AssistantAlertCarousel} owns the query
 * and the mutations.
 *
 * Renders nothing when handed no alerts, so the composer sits flush against the
 * transcript rather than against an empty frame.
 */
const AssistantAlertCarouselBase = ({
  alerts,
  selectedId,
  blockedNoticeAlertId,
  onSelect,
  onDismiss,
  onViewAll,
  className,
}: IAssistantAlertCarouselBaseProps) => {
  // `xl` is where the assistant becomes the 460px side panel; below it the pane
  // is the full-page route, which is the design's mobile header — count only, no
  // chevrons, because the track is swiped rather than stepped.
  const isPointerWidth = useBreakpoint('xl');

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Carousel.Root
      opts={{ align: 'start', containScroll: 'trimSnaps' }}
      // Trackpad/mouse-wheel horizontal scrolling; vertical wheel still scrolls the page
      plugins={[WheelGesturesPlugin()]}
      className={cx('flex flex-col', className)}
      aria-label={ALERT_SECTION_LABELS.needAttention}
    >
      {/*
       * Equal-basis outer cells, not `justify-between`: with three items of
       * unequal width, space-between places the middle one by leftover space, so
       * the pager sits off-centre by half the difference between the label and
       * the link. `flex-1` on both sides centres it whatever they measure.
       */}
      <div className="flex items-center gap-3 px-4 pt-4">
        <span className="text-secondary flex-1 text-sm font-medium">{ALERT_SECTION_LABELS.needAttention}</span>
        {alerts.length > 1 && <CarouselPager total={alerts.length} withChevrons={isPointerWidth} />}
        <div className="flex flex-1 justify-end">
          <button
            type="button"
            onClick={onViewAll}
            className="text-brand-secondary hover:text-brand-secondary_hover flex shrink-0 cursor-pointer items-center gap-0.5 text-sm font-medium"
          >
            View all
            {/*
             * Pulled out by the chevron box's own transparent right padding, so
             * its ink lands on the same 16px inset as the label's text on the
             * left. Without it the eye reads the right edge as ~5px further in.
             */}
            <ChevronRightIcon className="-mr-[5px] size-3.5 shrink-0" />
          </button>
        </div>
      </div>

      {/* Left inset only — the track bleeds to the panel's right edge so the
          next row peeks out from under the fade. */}
      <div className="relative py-3 pl-4">
        <Carousel.Content className="gap-2">
          {alerts.map((alert) => {
            // `timestamp` is deliberately dropped: at this width the row needs
            // both subtitle lines, and the Alerts Center is where alerts carry
            // a time.
            const { title, subtitle, status, tone } = calloutToAlertRow(alert);

            return (
              <Carousel.Item key={alert.id} className={PEEK_CLASS}>
                <AlertListItem
                  icon={<AlertTypeIcon callout={alert} />}
                  title={title}
                  subtitle={subtitle}
                  status={status}
                  tone={tone}
                  selected={alert.id === selectedId}
                  showBlockedNotice={alert.id === blockedNoticeAlertId}
                  onClick={onSelect ? () => onSelect(alert) : undefined}
                  onDismiss={onDismiss ? () => onDismiss(alert.id) : undefined}
                />
              </Carousel.Item>
            );
          })}
        </Carousel.Content>
        <TrackFade />
      </div>
    </Carousel.Root>
  );
};

export default AssistantAlertCarouselBase;
